/**
 * Sample jobs (onboarding redo, 2026-09-27; workshop Plan 1: open on a real
 * sample, and reach the first export fast).
 *
 * Every set offered ships a pre-trained job, so one click opens a job labelled
 * "Sample" with the simulated scanner loaded and no setup wait (owner decisions
 * 1 and 9, 2026-09-25). The choice is a picture and a word or two, in full rows.
 */

import { useEffect, useState } from 'react';
import { LoaderCircle, TriangleAlert } from 'lucide-react';
import type { SampleReaderDownloadProgress } from '../shared/desktop-api';
import { sampleSetIcon } from './sample-set-icons';
import { WorkspaceDialog } from './WorkspaceDialog';
import {
  sampleCardGrid,
  sampleSetChoices,
  sampleShortName,
  type SampleSetChoice,
} from './onboarding';
import './onboarding.css';

const iconFor = (corpus: string, size: number) => {
  const Icon = sampleSetIcon(corpus);
  return <Icon size={size} strokeWidth={1.4} />;
};

/** The installed sample sets that open without setup; null while reading. */
export function useSampleChoices(): SampleSetChoice[] | null {
  const [choices, setChoices] = useState<SampleSetChoice[] | null>(null);
  useEffect(() => {
    let live = true;
    const desktop = window.capturePointDesktop;
    if (!desktop?.getSimulatedScanners) {
      setChoices([]);
      return;
    }
    // The stack list is a directory read; no sample page is opened to fill it.
    void desktop
      .getSimulatedScanners()
      .then((catalog) => {
        if (live) setChoices(sampleSetChoices(catalog.stacks));
      })
      .catch(() => {
        if (live) setChoices([]);
      });
    return () => {
      live = false;
    };
  }, []);
  return choices;
}

/** Megabytes as the download screen says them: whole, never "0 of 0". */
const megabytes = (bytes: number) => Math.max(0, Math.round(bytes / 1_000_000));

/**
 * The calm wait while a sample job's reader weights download (owner decision 2026-09-30): the set's
 * picture, one line of progress, a thin meter, and a quiet way to open the job now with the standard
 * reader. The job opens by itself when the download finishes, or fails.
 */
export function SampleReaderDownload({
  choice,
  progress,
  onOpenNow,
}: {
  choice: SampleSetChoice;
  progress: Pick<SampleReaderDownloadProgress, 'receivedBytes' | 'totalBytes'>;
  onOpenNow: () => void;
}) {
  const [openingNow, setOpeningNow] = useState(false);
  const total = megabytes(progress.totalBytes);
  const received = Math.min(total, megabytes(progress.receivedBytes));
  const percent =
    progress.totalBytes > 0
      ? Math.min(100, (progress.receivedBytes / progress.totalBytes) * 100)
      : 0;
  const line = `Downloading sample reader… ${received} of ${total} MB`;
  return (
    <div className="sample-reader-download" role="status" aria-live="polite" aria-label={line}>
      <span className="sample-card-art" aria-hidden="true">
        {iconFor(choice.corpus, 38)}
      </span>
      <h3 className="sample-reader-download-title">Getting {sampleShortName(choice)} ready</h3>
      <p className="sample-reader-download-line">{line}</p>
      <span className="sample-reader-download-meter" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </span>
      <p className="sample-reader-download-note">
        The job opens by itself as soon as this finishes.
      </p>
      <button
        type="button"
        className="sample-reader-download-skip"
        disabled={openingNow}
        onClick={() => {
          setOpeningNow(true);
          onOpenNow();
        }}
      >
        {openingNow ? 'Opening…' : 'Open now with the standard reader'}
      </button>
    </div>
  );
}

/** Even rows of picture cards: one click starts that set's sample job. */
function SampleCards({
  choices,
  onChoose,
}: {
  choices: SampleSetChoice[];
  onChoose: (choice: SampleSetChoice) => Promise<void>;
}) {
  const [starting, setStarting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [download, setDownload] = useState<SampleReaderDownloadProgress | null>(null);
  useEffect(
    () =>
      window.capturePointDesktop?.onSampleReaderDownload?.((progress) =>
        setDownload(progress.state === 'downloading' ? progress : null),
      ),
    [],
  );
  const grid = sampleCardGrid(choices);
  const start = async (choice: SampleSetChoice) => {
    if (starting) return;
    setStarting(choice.corpus);
    setError('');
    try {
      await onChoose(choice);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'The sample job could not be started.');
      setStarting(null);
    } finally {
      setDownload(null);
    }
  };
  const downloading =
    download && starting === download.corpus
      ? choices.find((choice) => choice.corpus === starting)
      : undefined;
  if (download && downloading)
    return (
      <SampleReaderDownload
        choice={downloading}
        progress={download}
        onOpenNow={() =>
          void window.capturePointDesktop?.skipSampleReaderDownload?.(downloading.corpus)
        }
      />
    );
  return (
    <>
      <div
        className="sample-cards"
        role="group"
        aria-label="Kinds of documents"
        aria-busy={starting !== null}
        style={{ gridTemplateColumns: `repeat(${grid.columns}, minmax(0, 1fr))` }}
      >
        {grid.cards.map((choice) => (
          <button
            key={choice.corpus}
            type="button"
            className="sample-card"
            disabled={starting !== null}
            onClick={() => void start(choice)}
            title={choice.description}
            aria-label={`${choice.label}: ${choice.description}`}
          >
            <span className="sample-card-art" aria-hidden="true">
              {starting === choice.corpus ? (
                <LoaderCircle className="spin" size={34} />
              ) : (
                iconFor(choice.corpus, 38)
              )}
            </span>
            <span className="sample-card-name">{sampleShortName(choice)}</span>
          </button>
        ))}
      </div>
      {error && (
        <p className="sample-cards-error" role="alert" data-telemetry="error:sample-job-failed">
          <TriangleAlert size={15} aria-hidden="true" /> {error}
        </p>
      )}
    </>
  );
}

/** "Sample paperwork": from the welcome and New job's "A ready-made job". */
export function SampleJobsDialog({
  onChoose,
  onClose,
}: {
  onChoose: (choice: SampleSetChoice) => Promise<void>;
  onClose: () => void;
}) {
  const choices = useSampleChoices();
  return (
    <WorkspaceDialog
      telemetry="welcome-samples"
      title="What kind of documents do you work with?"
      onClose={onClose}
      compact
    >
      <div className="sample-jobs-dialog">
        {choices === null && (
          <p className="sample-cards-state" role="status">
            <LoaderCircle className="spin" size={16} /> Finding ready-made jobs…
          </p>
        )}
        {choices?.length === 0 && (
          <p className="sample-cards-state">
            <TriangleAlert size={16} aria-hidden="true" /> No ready-made jobs are installed on this
            computer.
          </p>
        )}
        {choices && choices.length > 0 && <SampleCards choices={choices} onChoose={onChoose} />}
      </div>
    </WorkspaceDialog>
  );
}
